import {
  For,
  Show,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
} from "solid-js";
import { A } from "@solidjs/router";
import { api } from "../api/client";
import type { BodyWeightLog, BodyWeightSummary } from "../api/types";
import WeightChart from "../components/WeightChart";
import PageHero from "../components/PageHero";
import Cell from "../components/tg/Cell";
import Section from "../components/tg/Section";
import { t } from "../i18n";
import {
  type WeightPeriod,
  invalidateBodyWeightCache,
  loadWeightPeriod,
} from "../lib/bodyWeightCache";
import { todayISO } from "../lib/repeatWorkout";
import { tgConfirm } from "../lib/telegram";

function formatDelta(n: number | null | undefined): string {
  if (n == null) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)} ${t("KG")}`;
}

function deltaClass(n: number | null | undefined): string {
  if (n == null || n === 0) return "text-ink-muted";
  return n < 0 ? "text-success" : "text-warning";
}

export default function WeightPage() {
  const [period, setPeriod] = createSignal<WeightPeriod>("30d");
  const [logs, setLogs] = createSignal<BodyWeightLog[]>([]);
  const [summary, setSummary] = createSignal<BodyWeightSummary | null>(null);
  const [loading, setLoading] = createSignal(true);

  const [date, setDate] = createSignal(todayISO());
  const [weight, setWeight] = createSignal("");
  const [notes, setNotes] = createSignal("");
  const [saving, setSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const [editing, setEditing] = createSignal<BodyWeightLog | null>(null);

  async function load(opts?: { force?: boolean }) {
    if (opts?.force) invalidateBodyWeightCache();
    setLoading(true);
    setError(null);
    try {
      const { logs: list, summary: sum } = await loadWeightPeriod(period());
      setLogs(list);
      setSummary(sum);
      if (!weight() && sum.current) {
        setWeight(String(sum.current.weight));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("ERROR_GENERIC"));
    } finally {
      setLoading(false);
    }
  }

  // Cache-first: period change only hits network when that period isn't cached
  createEffect(() => {
    const p = period();
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { logs: list, summary: sum } = await loadWeightPeriod(p);
        if (cancelled) return;
        setLogs(list);
        setSummary(sum);
        if (!weight() && sum.current) {
          setWeight(String(sum.current.weight));
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : t("ERROR_GENERIC"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    onCleanup(() => {
      cancelled = true;
    });
  });

  const current = createMemo(() => summary()?.current ?? null);

  async function save() {
    setError(null);
    const w = Number(weight().replace(",", "."));
    if (!Number.isFinite(w) || w <= 0) {
      setError(t("WEIGHT_INVALID"));
      return;
    }
    setSaving(true);
    try {
      if (editing()) {
        await api.updateBodyWeight(editing()!.id, {
          weight: w,
          notes: notes().trim() || null,
          date: date(),
        });
      } else {
        await api.upsertBodyWeight({
          date: date(),
          weight: w,
          notes: notes().trim() || null,
        });
      }
      setEditing(null);
      setNotes("");
      setDate(todayISO());
      await load({ force: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("ERROR_GENERIC"));
    } finally {
      setSaving(false);
    }
  }

  function startEdit(log: BodyWeightLog) {
    setEditing(log);
    setDate(log.date);
    setWeight(String(log.weight));
    setNotes(log.notes ?? "");
    setError(null);
  }

  function cancelEdit() {
    setEditing(null);
    setDate(todayISO());
    setNotes("");
    if (current()) setWeight(String(current()!.weight));
  }

  async function remove(id: string) {
    if (!(await tgConfirm(t("CONFIRM_DELETE")))) return;
    await api.deleteBodyWeight(id);
    if (editing()?.id === id) cancelEdit();
    await load({ force: true });
  }

  return (
    <div class="pb-4">
      <PageHero
        title={t("BODY_WEIGHT")}
        aside={
          <A href="/profile" class="btn-ghost px-2">
            {t("BACK")}
          </A>
        }
      />

      <Section>
        <Cell
          title={t("CURRENT_WEIGHT")}
          subtitle={current()?.date}
          value={
            current()
              ? `${current()!.weight.toFixed(1)} ${t("KG")}`
              : "—"
          }
        />
        <Cell
          title={t("VS_PREVIOUS")}
          value={
            <span class={deltaClass(summary()?.deltaVsPrevious)}>
              {formatDelta(summary()?.deltaVsPrevious)}
            </span>
          }
        />
        <Cell
          title={t("DELTA_7D")}
          value={
            <span class={deltaClass(summary()?.delta7d)}>
              {formatDelta(summary()?.delta7d)}
            </span>
          }
        />
        <Cell
          title={t("DELTA_30D")}
          value={
            <span class={deltaClass(summary()?.delta30d)}>
              {formatDelta(summary()?.delta30d)}
            </span>
          }
        />
      </Section>

      <Section header={editing() ? t("EDIT_WEIGHT") : t("LOG_WEIGHT")}>
        <div class="px-4 py-3 space-y-3">
          <div class="grid grid-cols-2 gap-2">
            <div>
              <label class="label">{t("DATE")}</label>
              <input
                class="input"
                type="date"
                value={date()}
                onInput={(e) => setDate(e.currentTarget.value)}
              />
            </div>
            <div>
              <label class="label">
                {t("WEIGHT")} ({t("KG")})
              </label>
              <input
                class="input tabular-nums"
                type="number"
                min={20}
                max={400}
                step={0.1}
                placeholder="75.5"
                value={weight()}
                onInput={(e) => setWeight(e.currentTarget.value)}
              />
            </div>
          </div>
          <div>
            <label class="label">
              {t("NOTES")} ({t("OPTIONAL")})
            </label>
            <input
              class="input"
              value={notes()}
              placeholder={t("WEIGHT_NOTES_HINT")}
              onInput={(e) => setNotes(e.currentTarget.value)}
            />
          </div>
          <Show when={error()}>
            <p class="text-[15px] text-[var(--destructive)]" role="alert">
              {error()}
            </p>
          </Show>
        </div>
      </Section>
      <div class="mb-4 flex gap-2">
        <Show when={editing()}>
          <button type="button" class="btn-secondary flex-1" onClick={cancelEdit}>
            {t("CANCEL")}
          </button>
        </Show>
        <button
          type="button"
          class="btn-primary flex-1"
          disabled={saving()}
          onClick={save}
        >
          {saving() ? t("LOADING") : t("SAVE")}
        </button>
      </div>
      <Show when={editing()}>
        <Section>
          <Cell
            title={t("DELETE")}
            destructive
            onClick={() => void remove(editing()!.id)}
          />
        </Section>
      </Show>

      <div class="tg-segmented mb-3" role="tablist" aria-label={t("WEIGHT_CHART")}>
        {(
          [
            ["30d", "PERIOD_30D"],
            ["90d", "PERIOD_90D"],
            ["all", "PERIOD_ALL"],
          ] as const
        ).map(([key, label]) => (
          <button
            type="button"
            role="tab"
            aria-selected={period() === key}
            onClick={() => setPeriod(key)}
          >
            {t(label)}
          </button>
        ))}
      </div>

      <Section header={t("WEIGHT_CHART")}>
        <div class="px-4 py-3">
          <Show when={loading()}>
            <p class="text-center text-[15px] text-ink-muted">{t("LOADING")}</p>
          </Show>
          <Show when={!loading()}>
            <WeightChart logs={logs()} />
          </Show>
        </div>
        <Show when={summary()?.period.count}>
          <Cell
            title={t("WEIGHT_MIN")}
            value={summary()!.period.min?.toFixed(1) ?? "—"}
          />
          <Cell
            title={t("WEIGHT_AVG")}
            value={summary()!.period.avg?.toFixed(1) ?? "—"}
          />
          <Cell
            title={t("WEIGHT_MAX")}
            value={summary()!.period.max?.toFixed(1) ?? "—"}
          />
        </Show>
      </Section>

      <Show when={!loading() && logs().length === 0}>
        <div class="empty-state">
          <p class="empty-state-hint">{t("NO_WEIGHT_DATA")}</p>
        </div>
      </Show>
      <Show when={!loading() && logs().length > 0}>
        <Section header={t("WEIGHT_HISTORY")}>
          <For each={logs()}>
            {(log) => (
              <Cell
                title={`${log.weight.toFixed(1)} ${t("KG")}`}
                subtitle={log.notes ? `${log.date} · ${log.notes}` : log.date}
                chevron
                onClick={() => startEdit(log)}
              />
            )}
          </For>
        </Section>
      </Show>
    </div>
  );
}
